import React, { useMemo, useState } from "react";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import {
	cloudColumns as baseColumns,
	Cloud,
	shapeCloudData,
	formatProviderName,
} from "./CloudConnectivityData";
import { shortSiteName } from "kontrolEdge/helpers/portLocation";
import { formatDataRate } from "kontrolEdge/helpers/formatDataRate";
import { useNavigate } from "react-router-dom";
import { GenericTable, GenericTableColumn } from "@ui-components/Table/GenericTable";
import GenericCard from "@ui-components/Card/GenericCard";
import { useCachedFetch } from "@hooks/useCachedFetch";
import { ListPageSkeleton, RefreshingIndicator } from "@shared/components/ListLoading";
import { useAppSelector } from "@redux/hooks";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { fetchCloudList } from "./requests/request";
import EmptyItems from "@shared/components/EmptyItems";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { makeCloudFields } from "./cloudFieldSpec";
import { normalizeFilters } from "kontrolEdge/helpers/normalizeFilters";
import { buildPredicate, ParsedFilter } from "kontrolEdge/helpers/advancedFilters";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import { useColumnVisibility } from "@ui-components/Table/useColumnVisibility";
import { ColumnVisibilityControl } from "@ui-components/Table/ColumnVisibilityControl";
import { isDemoClient } from "@shared/utils/clientEnv";

interface MainCloudConnectivity {
	text: string;
}

const NO_CLOUDS: Cloud[] = [];

const MainCloudConnectivity: React.FC = () => {
	const navigate = useNavigate();
	const { tenantId, partnerId } = useAppSelector(state => state.selection);
	const isViewingAllCustomers = tenantId === partnerId;
	const api = useAxiosAuth0();
	// Cached per tenant: coming back to the list paints the last answer at once and
	// refreshes it underneath.
	const {
		data: cloudList = NO_CLOUDS,
		error: fetchError,
		isLoading: isLoadingList,
		isRefreshing: isRefreshingList,
	} = useCachedFetch(tenantId ? `kontroledge/cloud-connectivity?tenantId=${tenantId}` : null, async () => {
		const { status, data } = await fetchCloudList(api, tenantId);
		if (status >= 500 || data.success === false) {
			throw new Error(data.message || "Unknown server error");
		}
		return Array.isArray(data.data) ? shapeCloudData(data.data) : [];
	});
	const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);

	const cloudFields = useMemo(
		() => makeCloudFields(isViewingAllCustomers),
		[isViewingAllCustomers]
	);

	const normalized = useMemo(
	() => normalizeFilters<Cloud>(activeFilters, cloudFields, {
		defaultStringToContains: true,   // strings default to contains
		quotedStringsExact: true,        // quotes => exact
	}),
	[activeFilters, cloudFields]
	);

	const predicate = useMemo(
	() => buildPredicate<Cloud>(normalized, cloudFields),
	[normalized, cloudFields]
	);

	const filteredData = useMemo(
	() => cloudList.filter(predicate),
	[cloudList, predicate]
	);

	const suggestedKeys = useMemo(
	() => cloudFields.map(f => f.label),
	[cloudFields]
	);


	// ------------------------------------------------------------------------------------
	// Override "Actions"
	// ------------------------------------------------------------------------------------
	// const columns = baseColumns.map((col) => {
	const columns = useMemo<GenericTableColumn<Cloud>[]>(() => {
				const cols = [...baseColumns];  
				if (isViewingAllCustomers) {
					cols.splice(0, 0, {
						header: "Customer",
						accessor: "customer",
						sortAccessor: (row: Cloud) => row.customer?.name ?? "",
						renderCell: (row: Cloud) =>
							row.customer ? (
							<div className="flex flex-col">
								<span className="font-medium">{row.customer.name}</span>
								<span className="text-xs text-gray-500">ID: {row.customer.chargebeeCustomerId}</span>
							</div>
							) : (
							"—"
							),
					});
				}
				// CASE A: Partner view (showing ALL customers) → the row's customer, not the lens.
				// CASE B: Customer view (only seeing your own) → the Redux-provided tenantId.
				const cloudDetailPath = (row: Cloud) => {
					const idFromRow = Number(row.customer?.id ?? NaN);
					const nextTenantId =
						isViewingAllCustomers && Number.isFinite(idFromRow) ? idFromRow : tenantId;
					return buildDetailPath("cloudConnectivity", { connectionId: row.id }, { tenantId: nextTenantId });
				};
				return cols.map(col => {
		// 1. Override the "Service ID" column to navigate on click
		if (col.header === "Connection ID") {
			return {
				...col,
				renderCell: (row: Cloud) => (
					<button
						{...viewHref(cloudDetailPath(row))}
						onClick={() => {
							// The tenant rides in the URL, not in history state, so a reload or a
							// pasted link keeps reading the connection in the customer's scope.
							navigate(cloudDetailPath(row), { state: { connectionDetail: row } });
						}}
						className="
						hover:text-semantic-text-brand 
						hover:underline
						focus:underline 
						active:underline
						font-medium
						underline
						text-semantic-text-brand"
						>
						{row.subscriptionId}
					</button>
				),
			};
		}

		// 2. Override the “Parent connection” column to navigate on click
		if (col.header === "Parent Connection") {
			return {
			...col,
			renderCell: (row: Cloud) => {
				const subidMatch = row.parentConnection?.match(/\[(PORT|LAG)\](.*)/);
				const subid = subidMatch ? subidMatch[2] : null;
			return (
				<button
				{...viewHref(buildDetailPath("port", { portId: row.parentConnectionId }, { subid }))}
				onClick={() => {
					navigate(
					buildDetailPath("port", { portId: row.parentConnectionId }, { subid }), {
					state: {
						portType: row.kontrolEdgePort?.portType as 'physical' | 'virtual' | undefined,
					},
				});
				}}
				className="
					hover:text-semantic-text-brand 
					hover:underline
					focus:underline 
					active:underline
					font-medium
					underline
					text-semantic-text-brand
				"
				>
				{row.parentConnection}
				</button>
			);
			},
		};
		}
		return col;
	});
		}, [baseColumns, isViewingAllCustomers]);

	const isDemo = isDemoClient();
	const cloudColumnVisibility = useColumnVisibility(
		"kontrol:cloud:columns",
		columns,
		["Connection ID"],
	);
	const tableColumns = isDemo ? cloudColumnVisibility.visibleColumns : columns;



	//During the fetching
	if (isLoadingList) {
		return <ListPageSkeleton />;
	}
	//If there's an error in the request
	if (fetchError) {
		return <ErrorFetchingItems text={fetchError} />;
	}
	// If no data, show empty state
	if (cloudList.length === 0) {
		return <EmptyItems />;
	}

	return (
		<div className="relative p-1 sm:p-4 sm:overflow-hidden flex flex-col sm:h-full">
			<RefreshingIndicator active={isRefreshingList} />

			<div className="relative flex-1 min-h-0">
				<GenericCard
					isCollapsible={false}
					className="h-full flex flex-col"
					bodyContent={
						<div className="flex flex-col h-full min-h-0">

							<div className="mb-2 sm:mb-4 bg-[var(--semantic-bg-primary)] flex-shrink-0">
								<div className="flex items-center justify-between gap-3 mt-0 mb-2 sm:my-5">
									<div className="w-full max-w-full sm:max-w-[500px]">
										<GenericSearchbar
											placeholder='Find by attribute or value'
											suggestedKeys={suggestedKeys}
											onFiltersChange={setActiveFilters}
											liveFiltering
											debounceMs={200}
										/>
									</div>
									{isDemo && (
										<ColumnVisibilityControl
											columns={columns}
											isVisible={cloudColumnVisibility.isVisible}
											toggle={cloudColumnVisibility.toggle}
											requiredIds={["Connection ID"]}
										/>
									)}
								</div>
							</div>

							<div className="flex-1 min-h-0 flex flex-col">
								<GenericTable
									columns={tableColumns}
									mobileCards={{
										titleColumn: "Connection ID",
										badgeColumn: "Connection Status",
										// Two lines a connection (CTX-1381).
										summary: (row) => [
											row.customer?.name,
											formatProviderName(row.provider),
											shortSiteName(row.kontrolEdgePort?.datacenter?.name),
											row.mbpsNum ? formatDataRate(row.mbpsNum) : null,
										],
										opensFromTitle: true,
										sortControl: true,
										itemNoun: "connection",
									}}
									data={filteredData}
									initialRowsPerPage={10}
									rowsPerPageOptions={[10, 20, 30]}
									width="100%"
									tableClassName="bg-semantic-bg-primary"
									tabletColumns={["Connection ID", "Connection Status", "Customer", "Provider", "Mbps"]}
									maxTableBodyHeight="100%"
								/>
							</div>
						</div>
					}
				/>
			</div>
		</div>
	);
};

export default MainCloudConnectivity;

